import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const SalesSignals = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [signals, setSignals] = useState([]);
  const [selectedSignal, setSelectedSignal] = useState(null);
  const [detectionInProgress, setDetectionInProgress] = useState(false);
  const [intentConfig, setIntentConfig] = useState({
    enabled: false,
    trackWebsiteVisits: true,
    trackContentDownloads: true,
    trackPricingPageViews: true,
    trackCompetitorComparisons: true,
    minConfidenceScore: 0.7,
    alertThreshold: 5
  });
  const [showIntentConfig, setShowIntentConfig] = useState(false);
  const [scoringRules, setScoringRules] = useState([
    { id: 1, name: 'Company Size', category: 'Firmographic', points: 20, condition: 'Employees > 500', enabled: true },
    { id: 2, name: 'Website Visits', category: 'Engagement', points: 10, condition: 'Visits > 5 in 7 days', enabled: true },
    { id: 3, name: 'Pricing Page View', category: 'Intent', points: 30, condition: 'Viewed pricing page', enabled: true },
    { id: 4, name: 'Content Download', category: 'Engagement', points: 15, condition: 'Downloaded whitepaper/case study', enabled: true },
    { id: 5, name: 'Email Opens', category: 'Engagement', points: 5, condition: 'Opened 3+ emails', enabled: true },
    { id: 6, name: 'Demo Request', category: 'Intent', points: 50, condition: 'Requested product demo', enabled: true },
    { id: 7, name: 'Industry Match', category: 'Firmographic', points: 15, condition: 'In target industry', enabled: true },
    { id: 8, name: 'Budget Authority', category: 'Firmographic', points: 25, condition: 'Decision maker role', enabled: false }
  ]);
  const [showScoringConfig, setShowScoringConfig] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (activeTab === 'signals') {
      loadSignals();
    }
  }, [activeTab]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/signals/dashboard');
      if (response.data.success) {
        setDashboard(response.data.dashboard);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSignals = async () => {
    try {
      const response = await api.get('/signals?acknowledged=false');
      if (response.data.success) {
        setSignals(response.data.signals);
      }
    } catch (error) {
      console.error('Error loading signals:', error);
    }
  };

  const detectSignals = async () => {
    try {
      setDetectionInProgress(true);
      const response = await api.post('/signals/detect', {
        lookback_days: 7
      });
      if (response.data.success) {
        alert(`✨ Detected ${response.data.signals_detected} new signals!`);
        loadDashboard();
        if (activeTab === 'signals') {
          loadSignals();
        }
      }
    } catch (error) {
      console.error('Error detecting signals:', error);
      alert('Failed to detect signals');
    } finally {
      setDetectionInProgress(false);
    }
  };

  const acknowledgeSignal = async (signalId) => {
    try {
      const response = await api.post(`/signals/${signalId}/acknowledge`);
      if (response.data.success) {
        loadSignals();
        loadDashboard();
        setSelectedSignal(null);
      }
    } catch (error) {
      console.error('Error acknowledging signal:', error);
      alert('Failed to acknowledge signal');
    }
  };

  const viewSignalDetail = async (signalId) => {
    try {
      const response = await api.get(`/signals/${signalId}`);
      if (response.data.success) {
        setSelectedSignal(response.data.signal);
      }
    } catch (error) {
      console.error('Error loading signal detail:', error);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#dc2626',
      high: '#ea580c',
      medium: '#f59e0b',
      low: '#3b82f6'
    };
    return colors[priority] || '#6b7280';
  };

  const getSignalTypeIcon = (type) => {
    const icons = {
      engagement_spike: '📈',
      intent_signal: '🎯',
      review_trend: '⭐',
      competitor_mention: '🔍',
      pricing_inquiry: '💰',
      feature_request: '✨',
      churn_risk: '⚠️',
      expansion_opportunity: '🚀',
      renewal_window: '🔔'
    };
    return icons[type] || '📊';
  };

  const getSignalTypeLabel = (type) => {
    const labels = {
      engagement_spike: 'Engagement Spike',
      intent_signal: 'Buying Intent',
      review_trend: 'Review Trend',
      competitor_mention: 'Competitor Mention',
      pricing_inquiry: 'Pricing Inquiry',
      feature_request: 'Feature Request',
      churn_risk: 'Churn Risk',
      expansion_opportunity: 'Expansion Opportunity',
      renewal_window: 'Renewal Window'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Sales Signals</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              📊 Sales Signals & Intent
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              AI-powered signal detection and buying intent tracking
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={detectSignals}
            disabled={detectionInProgress}
          >
            {detectionInProgress ? '🔄 Detecting...' : '🔍 Detect Signals'}
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            gap: 'var(--space-1)'
          }}>
            {[
              { key: 'overview', label: '📈 Overview' },
              { key: 'signals', label: '🎯 Active Signals' },
              { key: 'intent', label: '💡 Intent Data' },
              { key: 'scoring', label: '⭐ Lead Scoring' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'none',
                  border: 'none',
                  borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: activeTab === tab.key ? 'var(--primary-600)' : 'var(--gray-500)',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboard && (
          <>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>Dashboard Overview</h3>

            {/* Priority Breakdown */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {Object.entries(dashboard.priority_breakdown).map(([priority, count]) => (
                <div
                  key={priority}
                  style={{
                    padding: '1.5rem',
                    border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    borderLeft: `4px solid ${getPriorityColor(priority)}`,
                    backgroundColor: isDark ? '#374151' : 'var(--gray-50)'
                  }}
                >
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: getPriorityColor(priority), marginBottom: '0.5rem' }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', textTransform: 'capitalize' }}>
                    {priority} Priority
                  </div>
                </div>
              ))}
            </div>

            {/* Signal Type Breakdown */}
            <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Signals by Type</h4>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmin(250px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {Object.entries(dashboard.type_breakdown).map(([type, count]) => (
                <div
                  key={type}
                  style={{
                    padding: '1rem',
                    border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#1f2937' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem'
                  }}
                >
                  <div style={{ fontSize: '2rem' }}>{getSignalTypeIcon(type)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                      {getSignalTypeLabel(type)}
                    </div>
                    <div style={{ fontSize: '1.25rem', color: 'var(--primary-600)', fontWeight: 'bold' }}>
                      {count}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Signals */}
            <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Recent Signals</h4>
            {dashboard.recent_signals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                <p>No active signals. Click "Detect Signals" to analyze recent data.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {dashboard.recent_signals.map(signal => (
                  <div
                    key={signal.id}
                    style={{
                      padding: '1rem',
                      border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                      borderRadius: '8px',
                      borderLeft: `4px solid ${getPriorityColor(signal.priority)}`,
                      backgroundColor: isDark ? '#1f2937' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onClick={() => {
                      setActiveTab('signals');
                      viewSignalDetail(signal.id);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'var(--gray-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? '#1f2937' : 'white'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>{getSignalTypeIcon(signal.signal_type)}</span>
                        <div>
                          <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>{signal.title}</div>
                          <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                            {new Date(signal.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: `${getPriorityColor(signal.priority)}20`,
                        color: getPriorityColor(signal.priority),
                        textTransform: 'uppercase'
                      }}>
                        {signal.priority}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.875rem' }}>
                      <span>Confidence: <strong>{(signal.confidence * 100).toFixed(0)}%</strong></span>
                      <span style={{ color: 'var(--primary-600)' }}>View Details →</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Active Signals Tab */}
        {activeTab === 'signals' && (
          <>
            {selectedSignal ? (
              /* Signal Detail View */
              <div>
                <button
                  onClick={() => setSelectedSignal(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-600)',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                    padding: '0.5rem 0',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  ← Back to Signals List
                </button>

                <div style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  borderLeft: `4px solid ${getPriorityColor(selectedSignal.priority)}`,
                  padding: '1.5rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '2rem' }}>{getSignalTypeIcon(selectedSignal.signal_type)}</span>
                      <div>
                        <h3 style={{ margin: '0 0 0.25rem 0' }}>{selectedSignal.title}</h3>
                        <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          {getSignalTypeLabel(selectedSignal.signal_type)} • {new Date(selectedSignal.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      backgroundColor: `${getPriorityColor(selectedSignal.priority)}20`,
                      color: getPriorityColor(selectedSignal.priority),
                      textTransform: 'uppercase'
                    }}>
                      {selectedSignal.priority}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: 'var(--gray-50)',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <p style={{ margin: 0, lineHeight: '1.6' }}>{selectedSignal.description}</p>
                  </div>

                  {/* Signal Details Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Confidence Score
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                        {(selectedSignal.confidence * 100).toFixed(0)}%
                      </div>
                    </div>
                    {selectedSignal.signal_data && Object.entries(selectedSignal.signal_data).map(([key, value]) => (
                      <div key={key}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                          {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Suggested Actions */}
                  {selectedSignal.suggested_actions && selectedSignal.suggested_actions.length > 0 && (
                    <div>
                      <h4 style={{ marginBottom: '0.75rem' }}>💡 Suggested Actions</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {selectedSignal.suggested_actions.map((action, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: '0.75rem',
                              backgroundColor: 'var(--gray-50)',
                              borderRadius: '8px',
                              border: '1px solid var(--gray-200)'
                            }}
                          >
                            {action}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Acknowledge Button */}
                  {!selectedSignal.acknowledged_at && (
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--gray-200)' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => acknowledgeSignal(selectedSignal.id)}
                      >
                        ✓ Acknowledge Signal
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* Signals List */
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Active Signals</h3>
                {signals.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--gray-600)' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
                    <p>No active signals found.</p>
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: '1rem' }}
                      onClick={detectSignals}
                      disabled={detectionInProgress}
                    >
                      {detectionInProgress ? '🔄 Detecting...' : '🔍 Detect Signals Now'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {signals.map(signal => (
                      <div
                        key={signal.id}
                        style={{
                          padding: '1.25rem',
                          border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                          borderRadius: '8px',
                          borderLeft: `4px solid ${getPriorityColor(signal.priority)}`,
                          backgroundColor: isDark ? '#1f2937' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onClick={() => viewSignalDetail(signal.id)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'var(--gray-50)'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? '#1f2937' : 'white'}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ fontSize: '1.75rem' }}>{getSignalTypeIcon(signal.signal_type)}</span>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '1.05rem', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                                {signal.title}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                                {getSignalTypeLabel(signal.signal_type)} • {new Date(signal.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: `${getPriorityColor(signal.priority)}20`,
                            color: getPriorityColor(signal.priority),
                            textTransform: 'uppercase'
                          }}>
                            {signal.priority}
                          </div>
                        </div>
                        <div style={{ color: isDark ? '#d1d5db' : 'var(--gray-700)', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                          {signal.description}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          <span>Confidence: <strong style={{ color: 'var(--primary-600)' }}>{(signal.confidence * 100).toFixed(0)}%</strong></span>
                          <span style={{ color: 'var(--primary-600)', fontWeight: '600' }}>View Details →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Intent Data Tab */}
        {activeTab === 'intent' && (
          <div>
            {!showIntentConfig ? (
              <>
                <div style={{ marginBottom: '2rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h3 style={{ margin: 0 }}>Intent Tracking Configuration</h3>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowIntentConfig(true)}
                    >
                      ⚙️ Configure Tracking
                    </button>
                  </div>

                  {/* Status Card */}
                  <div style={{
                    padding: '1.5rem',
                    border: `2px solid ${intentConfig.enabled ? '#10b981' : '#f59e0b'}`,
                    borderRadius: '8px',
                    backgroundColor: intentConfig.enabled 
                      ? (isDark ? '#065f46' : '#f0fdf4') 
                      : (isDark ? '#78350f' : '#fffbeb'),
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '2rem' }}>
                        {intentConfig.enabled ? '✅' : '⚠️'}
                      </span>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                          {intentConfig.enabled ? 'Intent Tracking Active' : 'Intent Tracking Disabled'}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'var(--gray-600)' }}>
                          {intentConfig.enabled 
                            ? 'Monitoring buyer intent signals across all channels'
                            : 'Enable tracking to start capturing buying intent signals'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tracking Sources */}
                  <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Active Tracking Sources</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                    {[
                      { key: 'trackWebsiteVisits', label: 'Website Visits', icon: '🌐', desc: 'Track page views and time on site' },
                      { key: 'trackContentDownloads', label: 'Content Downloads', icon: '📥', desc: 'Monitor whitepaper and resource downloads' },
                      { key: 'trackPricingPageViews', label: 'Pricing Page Views', icon: '💰', desc: 'Detect pricing interest signals' },
                      { key: 'trackCompetitorComparisons', label: 'Competitor Research', icon: '🔍', desc: 'Identify comparison searches' }
                    ].map(source => (
                      <div
                        key={source.key}
                        style={{
                          padding: '1.25rem',
                          border: `1px solid ${intentConfig[source.key] ? 'var(--primary-600)' : (isDark ? '#4b5563' : 'var(--gray-200)')}`,
                          borderRadius: '8px',
                          backgroundColor: intentConfig[source.key] 
                            ? (isDark ? '#1e3a5f' : '#eff6ff') 
                            : (isDark ? '#1f2937' : 'white')
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>{source.icon}</span>
                          <div style={{ fontWeight: '600', color: isDark ? '#f9fafb' : 'inherit' }}>{source.label}</div>
                          <div style={{ 
                            marginLeft: 'auto',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: intentConfig[source.key] ? '#10b981' : '#9ca3af',
                            color: 'white'
                          }}>
                            {intentConfig[source.key] ? 'ON' : 'OFF'}
                          </div>
                        </div>
                        <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                          {source.desc}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Settings Summary */}
                  <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Detection Settings</h4>
                  <div style={{
                    padding: '1.25rem',
                    border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#374151' : 'var(--gray-50)'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.25rem' }}>
                          Minimum Confidence Score
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                          {(intentConfig.minConfidenceScore * 100).toFixed(0)}%
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.25rem' }}>
                          Alert Threshold
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                          {intentConfig.alertThreshold} signals
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Configuration Form */
              <div>
                <button
                  onClick={() => setShowIntentConfig(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-600)',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                    padding: '0.5rem 0',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  ← Back to Overview
                </button>

                <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>Configure Intent Tracking</h3>

                <div style={{
                  padding: '1.5rem',
                  border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#1f2937' : 'white',
                  marginBottom: '1.5rem'
                }}>
                  {/* Enable/Disable Toggle */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingBottom: '1.5rem',
                    borderBottom: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                    marginBottom: '1.5rem'
                  }}>
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>Enable Intent Tracking</div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        Start monitoring buyer intent signals across all sources
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={intentConfig.enabled}
                        onChange={(e) => setIntentConfig({...intentConfig, enabled: e.target.checked})}
                        style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                      />
                    </label>
                  </div>

                  {/* Tracking Sources */}
                  <h4 style={{ marginBottom: '1rem' }}>Tracking Sources</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                    {[
                      { key: 'trackWebsiteVisits', label: 'Website Visits & Page Views' },
                      { key: 'trackContentDownloads', label: 'Content Downloads (PDFs, Whitepapers)' },
                      { key: 'trackPricingPageViews', label: 'Pricing Page Views' },
                      { key: 'trackCompetitorComparisons', label: 'Competitor Comparison Searches' }
                    ].map(source => (
                      <label key={source.key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={intentConfig[source.key]}
                          onChange={(e) => setIntentConfig({...intentConfig, [source.key]: e.target.checked})}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.95rem' }}>{source.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Detection Settings */}
                  <h4 style={{ marginBottom: '1rem' }}>Detection Settings</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Minimum Confidence Score: {(intentConfig.minConfidenceScore * 100).toFixed(0)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={intentConfig.minConfidenceScore}
                        onChange={(e) => setIntentConfig({...intentConfig, minConfidenceScore: parseFloat(e.target.value)})}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                        Only create signals with confidence above this threshold
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Alert Threshold: {intentConfig.alertThreshold} signals
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="20"
                        step="1"
                        value={intentConfig.alertThreshold}
                        onChange={(e) => setIntentConfig({...intentConfig, alertThreshold: parseInt(e.target.value)})}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                        Send notification when this many new signals are detected
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      alert('✓ Intent tracking configuration saved!');
                      setShowIntentConfig(false);
                    }}
                  >
                    Save Configuration
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowIntentConfig(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lead Scoring Tab */}
        {activeTab === 'scoring' && (
          <div>
            {!showScoringConfig ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: 0 }}>Lead Scoring Rules</h3>
                  <button 
                    className="btn btn-primary"
                    onClick={() => setShowScoringConfig(true)}
                  >
                    ➕ Add New Rule
                  </button>
                </div>

                {/* Scoring Overview */}
                <div style={{
                  padding: '1.5rem',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--gray-50)',
                  marginBottom: '2rem'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Active Rules
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                        {scoringRules.filter(r => r.enabled).length}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Maximum Score
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                        {scoringRules.filter(r => r.enabled).reduce((sum, r) => sum + r.points, 0)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Categories
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                        {new Set(scoringRules.map(r => r.category)).size}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rules by Category */}
                {['Firmographic', 'Engagement', 'Intent'].map(category => {
                  const categoryRules = scoringRules.filter(r => r.category === category);
                  if (categoryRules.length === 0) return null;
                  
                  return (
                    <div key={category} style={{ marginBottom: '2rem' }}>
                      <h4 style={{ 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>{category === 'Firmographic' ? '🏢' : category === 'Engagement' ? '📊' : '🎯'}</span>
                        {category} Rules
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: 'normal',
                          color: 'var(--gray-600)',
                          marginLeft: '0.5rem'
                        }}>
                          ({categoryRules.filter(r => r.enabled).length} active)
                        </span>
                      </h4>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {categoryRules.map(rule => (
                          <div
                            key={rule.id}
                            style={{
                              padding: '1.25rem',
                              border: `1px solid ${rule.enabled ? 'var(--primary-600)' : (isDark ? '#4b5563' : 'var(--gray-200)')}`,
                              borderRadius: '8px',
                              backgroundColor: rule.enabled 
                                ? (isDark ? '#1e3a5f' : '#eff6ff') 
                                : (isDark ? '#1f2937' : 'white'),
                              opacity: rule.enabled ? 1 : 0.6
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                                  <div style={{ fontWeight: '600', fontSize: '1.05rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                                    {rule.name}
                                  </div>
                                  <div style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '12px',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    backgroundColor: 'var(--primary-600)',
                                    color: 'white'
                                  }}>
                                    +{rule.points} pts
                                  </div>
                                  <div style={{
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    backgroundColor: rule.enabled ? '#10b981' : '#9ca3af',
                                    color: 'white'
                                  }}>
                                    {rule.enabled ? 'ACTIVE' : 'DISABLED'}
                                  </div>
                                </div>
                                <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                                  {rule.condition}
                                </div>
                              </div>
                              
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                  onClick={() => {
                                    setEditingRule(rule);
                                    setShowScoringConfig(true);
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                                    borderRadius: '6px',
                                    backgroundColor: isDark ? '#374151' : 'white',
                                    color: isDark ? '#e5e7eb' : 'inherit',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  ✏️ Edit
                                </button>
                                <button
                                  onClick={() => {
                                    setScoringRules(scoringRules.map(r => 
                                      r.id === rule.id ? {...r, enabled: !r.enabled} : r
                                    ));
                                  }}
                                  style={{
                                    padding: '0.5rem 1rem',
                                    border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}`,
                                    borderRadius: '6px',
                                    backgroundColor: isDark ? '#374151' : 'white',
                                    color: isDark ? '#e5e7eb' : 'inherit',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {rule.enabled ? '⏸️ Disable' : '▶️ Enable'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              /* Add/Edit Rule Form */
              <div>
                <button
                  onClick={() => {
                    setShowScoringConfig(false);
                    setEditingRule(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-600)',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                    padding: '0.5rem 0',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  ← Back to Rules
                </button>

                <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                  {editingRule ? 'Edit Scoring Rule' : 'Add New Scoring Rule'}
                </h3>

                <div style={{
                  padding: '1.5rem',
                  border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-200)'}`,
                  borderRadius: '8px',
                  backgroundColor: isDark ? '#1f2937' : 'white'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                        Rule Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Enterprise Company Size"
                        defaultValue={editingRule?.name || ''}
                        style={{ width: '100%' }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Category
                      </label>
                      <select 
                        className="form-control"
                        defaultValue={editingRule?.category || 'Engagement'}
                        style={{ width: '100%' }}
                      >
                        <option value="Firmographic">🏢 Firmographic</option>
                        <option value="Engagement">📊 Engagement</option>
                        <option value="Intent">🎯 Intent</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Points
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="e.g., 20"
                        min="1"
                        max="100"
                        defaultValue={editingRule?.points || 10}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                        Points awarded when this condition is met
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Condition
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g., Company has 500+ employees"
                        defaultValue={editingRule?.condition || ''}
                        style={{ width: '100%' }}
                      />
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                        Describe when this rule should be triggered
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          defaultChecked={editingRule?.enabled !== false}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '600' }}>Enable this rule</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      alert(editingRule ? '✓ Rule updated successfully!' : '✓ New rule added successfully!');
                      setShowScoringConfig(false);
                      setEditingRule(null);
                    }}
                  >
                    {editingRule ? 'Update Rule' : 'Create Rule'}
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowScoringConfig(false);
                      setEditingRule(null);
                    }}
                  >
                    Cancel
                  </button>
                  {editingRule && (
                    <button 
                      style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #dc2626',
                        borderRadius: '6px',
                        backgroundColor: isDark ? '#7f1d1d' : 'white',
                        color: isDark ? '#fca5a5' : '#dc2626',
                        cursor: 'pointer',
                        fontWeight: '600',
                        marginLeft: 'auto'
                      }}
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this rule?')) {
                          setScoringRules(scoringRules.filter(r => r.id !== editingRule.id));
                          setShowScoringConfig(false);
                          setEditingRule(null);
                        }
                      }}
                    >
                      🗑️ Delete Rule
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesSignals;
